import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { ContentObjectTypesTable } from "./ContentObjectTypesTable";
import { useWatchSearchResult } from "./search/ObjectTypeSearchContext";
import { EmptyCollection, ErrorBox, Input, SelectBox, useDebounce, useIntersectionObserver, useToast } from "@vertesia/ui/core";
import { useUserSession } from "@vertesia/ui/session";
import { useTypeRegistry } from "./TypeRegistryProvider.js";
import { useUITranslation } from '../../../i18n/index.js';
import { CreateOrUpdateTypeModal } from "./CreateOrUpdateTypeModal";
var ChunkableOptions;
(function (ChunkableOptions) {
    ChunkableOptions["true"] = "Yes";
    ChunkableOptions["false"] = "No";
})(ChunkableOptions || (ChunkableOptions = {}));
;
export function ContentObjectTypesSearch({ isDirty = false }) {
    const { store } = useUserSession();
    const { reload: reloadTypes } = useTypeRegistry();
    const { t } = useUITranslation();
    const toast = useToast();
    const [isReady, setIsReady] = useState(false);
    const { search, isLoading, error, objects } = useWatchSearchResult();
    const [searchTerm, setSearchTerm] = useState("");
    const debounceValue = useDebounce(searchTerm, 500);
    const loadMoreRef = useRef(null);
    useIntersectionObserver(loadMoreRef, () => {
        isReady && search.loadMore();
    }, { deps: [isReady] });
    useEffect(() => {
        search.search()
            .then(() => setIsReady(true));
    }, []);
    useEffect(() => {
        search.query.name = searchTerm;
        search.search()
            .then(() => setIsReady(true));
    }, [debounceValue]);
    const [chunkable, setChunkable] = useState(undefined);
    const onChunkableChange = (data) => {
        setChunkable(data);
    };
    useEffect(() => {
        search.query.chunkable = chunkable ? chunkable == "Yes" : undefined;
        search.search()
            .then(() => setIsReady(true));
    }, [chunkable]);
    useEffect(() => {
        if (isDirty && isReady) {
            search.search().then(() => setIsReady(true));
        }
    }, [isDirty]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const onOpenCreateModal = () => {
        setShowCreateModal(true);
    };
    if (error) {
        return (_jsx(ErrorBox, { title: t('store.failedToFetchTypes'), children: error.message }));
    }
    ;
    const onCloseCreateModal = async (payload) => {
        if (!payload) {
            setShowCreateModal(false);
            return Promise.resolve();
        }
        return store.types.create(payload).then(async () => {
            toast({
                status: "success",
                title: t('store.typeCreated'),
                duration: 2000
            });
            reloadTypes();
            search.search().then(() => setIsReady(true));
        }).catch(err => {
            toast({
                status: "error",
                title: t('store.errorCreatingType'),
                description: err.message,
                duration: 5000
            });
        });
    };
    return (_jsxs("div", { className: "flex flex-col flex-1 min-h-0", children: [_jsxs("div", { className: "flex flex-shrink-0 gap-4", children: [_jsx(Input, { placeholder: t('store.filterByName'), value: searchTerm, onChange: setSearchTerm }), _jsx(SelectBox, { className: "w-60", isClearable: true, options: Object.values(ChunkableOptions), value: chunkable, onChange: onChunkableChange, placeholder: t('store.isChunkable') })] }), _jsx("div", { className: "flex flex-col w-full flex-1 min-h-0 border rounded-md my-2", children: _jsxs("div", { className: "flex-1 min-h-0 overflow-y-auto", children: [(!isLoading && objects?.length === 0) ? (_jsx(EmptyCollection, { title: t('store.noType'), buttonLabel: t('store.createType'), onClick: onOpenCreateModal, children: t('store.getStartedTypes') })) : (_jsxs(_Fragment, { children: [_jsx(ContentObjectTypesTable, { objects: objects, isLoading: isLoading }), _jsx("div", { ref: loadMoreRef, className: "h-4 w-full" })] })), _jsx(CreateOrUpdateTypeModal, { okLabel: "Create", title: t('store.createType'), isOpen: showCreateModal, onClose: onCloseCreateModal })] }) })] }));
}
//# sourceMappingURL=ContentObjectTypesSearch.js.map